import React, { useEffect, useState } from 'react';
import PageContainer from '../../../../components/PageContainer';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import { AccountTreeOutlined, BadgeOutlined, BlockOutlined, CheckCircleOutline, EditOutlined, PersonAdd, WorkspacePremiumOutlined } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import appRoutes from '../../../../routes/routeConfig';
import Filters from '../../../../components/Filters';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import { getPrivilegeStatus, getStatusColor, testUsers } from '../../../../util';
import { deactivateUser, getUsers } from '../../../../redux/api/userMgt';
import { useSnackbar } from 'notistack';
import { getAllInstitutions } from '../../../../redux/api/institution';

const ExternalUsers = () => {
    const [processing, setProcessing] = useState(false);
    const [performingAction, setPerformingAction] = useState(false);
    const [actionModal, setActionModal] = useState(null); // activate || deactivate
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [institutions, setInstitutions] = useState([]);

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.id) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        {getPrivilegeStatus('getUserDetailsById') && (
                            <IconButton
                                size="small"
                                color="primary"
                                variant="outlined"
                                title="Edit"
                                onClick={() => {
                                    history.push(`/internal/users/external/user/${params?.row?.id}`);
                                }}
                            >
                                <EditOutlined fontSize="small" />
                            </IconButton>
                        )}

                        {getPrivilegeStatus('deactivateUser') && (
                            <IconButton
                                size="small"
                                color={Boolean(params?.row?.isActive) ? 'error' : 'primary'}
                                onClick={() => {
                                    setActionModal(params?.row || null);
                                }}
                                variant="outlined"
                                title={Boolean(params?.row?.isActive) ? 'Deactivate' : 'Activate'}
                            >
                                {Boolean(params?.row?.isActive) ? <BlockOutlined fontSize="small" /> : <CheckCircleOutline fontSize="small" />}
                            </IconButton>
                        )}
                    </div>
                );
            },
        },
        { field: 'username', headerName: 'Username', width: 250 },
        { field: 'firstName', headerName: 'First Name', width: 200 },
        { field: 'lastName', headerName: 'Last Name', width: 200 },
        {
            field: 'account_status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => <Chip size="small" color={getStatusColor(params?.row?.isActive ? 'active' : 'inactive')} label={<small>{params?.row?.isActive ? 'Active' : 'Inactive'}</small>} />,
        },
        { field: 'roles', headerName: 'Roles', width: 200 },
        { field: 'institutionName', headerName: 'Institution', width: 250 },
        { field: 'email', headerName: 'Email', width: 250 },
        { field: 'employeeId', headerName: 'Employee Id', width: 150 },
        { field: 'phoneNumber', headerName: 'Phone Number', width: 150 },
        {
            field: 'createdDate',
            headerName: 'Creation Date',
            width: 200,
            valueGetter: (params) => (params?.row?.createdDate ? moment(params?.row?.createdDate).format('DD MMMM, YYYY') : ''),
        },
    ];

    const performAction = async () => {
        const status = !Boolean(actionModal?.isActive);
        try {
            setPerformingAction(true);
            await deactivateUser({
                id: actionModal?.id,
                isActive: status,
            });
            setActionModal(null);
            enqueueSnackbar(`User ${!status && 'de'}activated successfully`);
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setPerformingAction(false);
        }
    };

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getUsers('external');
            setRecords(res ?? []);
            setSearchResults(res ?? []);

            await getAllInstitutions()
                .then((res) => {
                    setInstitutions(res?.resultObject || []);
                })
                .catch((err) => console.log(err));
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleSearch = (values) => {
        setSearchResults(
            records.filter((record) => {
                let valid = true;
                if (values.name) {
                    valid = (valid && record.firstName.toLowerCase().includes(values.name.toLowerCase())) || record.lastName.toLowerCase().includes(values.name.toLowerCase());
                }

                if (values.institutionId) {
                    valid = valid && record.institutionId == values.institutionId;
                }

                if (values.username) {
                    valid = valid && record.username.toLowerCase().includes(values.username.toLowerCase());
                }

                return valid;
            })
        );
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="External Users"
            tagline="Manage all external users"
            processing={processing}
            buttonComponent={
                <>
                    <Link to={appRoutes.internal.users.createExternal}>
                        <Button startIcon={<PersonAdd />} style={{ margin: '0 .5rem' }} variant="contained">
                            Create External User
                        </Button>
                    </Link>
                </>
            }
        >
            <Filters
                searchFields={[
                    { id: 'username', label: 'Username' },
                    { id: 'name', label: 'Name' },
                    { id: 'institutionId', type: 'select', label: 'Institution', field: 'institutionId', fieldDisplay: 'name', options: institutions },
                ]}
                searchFunction={handleSearch}
                useDefault={false}
                showClearBtn={false}
            />

            <Box>
                <div className="paper" style={{ height: 650, width: '100%', textAlign: 'center' }}>
                    <DataGrid
                        className={'center-first-column'}
                        rows={searchResults}
                        getRowId={(row) => row.id}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        style={{ fontSize: '12px' }}
                    />
                </div>
            </Box>

            <Dialog open={Boolean(actionModal)} onClose={() => !performingAction && setActionModal(null)} maxWidth="xs" fullWidth>
                <DialogContent>
                    <h4>{Boolean(actionModal?.isActive) ? 'Deactivate' : 'Activate'} User</h4>
                    <p>
                        Kindly confirm {Boolean(actionModal?.isActive) && 'de'}
                        activation of user account with details below:
                        <ul>
                            <li>Email: {actionModal?.email}</li>
                            <li>
                                Full name: {actionModal?.lastName} {actionModal?.firstName} {actionModal?.middleName}
                            </li>
                            <li>EmployeeId: {actionModal?.employeeId}</li>
                        </ul>
                    </p>
                </DialogContent>
                <DialogActions className="pb-1 pr-1">
                    <Button variant="contained" color="secondary" onClick={() => setActionModal(null)} disabled={performingAction}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={performAction} disabled={performingAction}>
                        {Boolean(performingAction) ? <CircularProgress color="inherit" size={15} /> : Boolean(actionModal?.isActive) ? 'Deactivate' : 'Activate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default ExternalUsers;
